import { z } from 'zod';
import type { LocationProviderType } from '../enums/location.js';
import { KolSearchResultModel } from './kol.js';
import type { GeoPoint } from './location.js';
import type { AWSStep } from './smart-routing.aws.js';

export type IdentifiedPoint = {
  id: string;
} & GeoPoint;

type AWSLegMeta = {
  steps: AWSStep[];
};

type LegMetaMap = {
  [LocationProviderType.AWS]: AWSLegMeta;
  [LocationProviderType.Azure]: { points: GeoPoint[] };
};

type LegMeta<T extends LocationProviderType> = LegMetaMap[T];

export type RouteLeg<T extends LocationProviderType> = {
  start: IdentifiedPoint;
  end: IdentifiedPoint;
  distanceMiles: number;
  durationSeconds: number;
  meta: LegMeta<T>;
};

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

const ICoordsSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

/** Entity type discriminator for v2 Smart Routing entities */
export const SmartRoutingEntityType = z.enum(['hcp', 'account']);
export type SmartRoutingEntityType = z.infer<typeof SmartRoutingEntityType>;

/** Normalized entity reference used in v2 selections and route legs */
export const SmartRoutingEntitySchema = z.object({
  entityId: z.union([z.string(), z.number()]),
  entityType: SmartRoutingEntityType,
  displayName: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  /** Provider-specific metadata (specialty, classification, etc.) */
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type SmartRoutingEntity = z.infer<typeof SmartRoutingEntitySchema>;

// ---------------------------------------------------------------------------
// Starting location (shared across versions)
// ---------------------------------------------------------------------------

const StartingLocationSchema = z.union([
  z
    .object({
      type: z.literal('search'),
      id: z.string(),
      name: z.string(),
    })
    .extend(ICoordsSchema.shape),
  z
    .object({
      type: z.literal('browser'),
    })
    .extend(ICoordsSchema.shape),
  z
    .object({
      type: z.literal('hcp'),
      id: z.number(),
      name: z.string(),
    })
    .extend(ICoordsSchema.shape),
  z
    .object({
      type: z.literal('entity'),
      entityId: z.string(),
      entityType: SmartRoutingEntityType,
      entityName: z.string(),
    })
    .extend(ICoordsSchema.shape),
]);

export type SmartRoutingStartingLocation = z.infer<typeof StartingLocationSchema>;

// ---------------------------------------------------------------------------
// Status enum (shared across versions)
// ---------------------------------------------------------------------------

export const SmartRoutingStatusEnum = z.enum([
  'select-location',
  'submit-location',
  'select-hcps',
  'select-entities',
  'submit-route',
  'route-generated',
]);
export type SmartRoutingStatus = z.infer<typeof SmartRoutingStatusEnum>;

// ---------------------------------------------------------------------------
// Route leg schemas
// ---------------------------------------------------------------------------

const LocationEntitySchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  type: SmartRoutingEntityType,
});
export type LocationEntity = z.infer<typeof LocationEntitySchema>;

export const LocationInfoSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  address: z.string().nullable(),
  entity: LocationEntitySchema.nullable(),
});
export type LocationInfo = z.infer<typeof LocationInfoSchema>;

const LocationRoutingLegSchema = z.object({
  start: LocationInfoSchema,
  end: LocationInfoSchema,
  distanceMiles: z.number(),
  durationSeconds: z.number(),
  meta: z.any(),
});

const EntityRoutingLegSchema = z.object({
  start: LocationInfoSchema,
  end: LocationInfoSchema,
  distanceMiles: z.number(),
  durationSeconds: z.number(),
  meta: z.any(),
});
export type SmartRoutingEntityLeg = z.infer<typeof EntityRoutingLegSchema>;

// ---------------------------------------------------------------------------
// v1 schema (legacy — preserved for backward compatibility)
// ---------------------------------------------------------------------------

export const SmartRoutingV1Schema = z.object({
  version: z.literal(1).optional(),
  status: SmartRoutingStatusEnum.nullable(),
  startingLocation: StartingLocationSchema.nullable(),
  /** @deprecated Use selectedEntities in v2 */
  selectedHcps: z.array(z.number()).nullable(),
  routingResult: z
    .object({
      legs: z.array(LocationRoutingLegSchema),
      provider: z.string(),
    })
    .nullable(),
  /** @deprecated Use subjectEntityId in v2 */
  hcpId: z.string().nullable().optional(),
  hideLocationForm: z.boolean().optional(),
  /** @deprecated Not used in v2 */
  kolProfile: KolSearchResultModel.nullable().optional(),
});
export type SmartRoutingV1 = z.infer<typeof SmartRoutingV1Schema>;

// ---------------------------------------------------------------------------
// v2 schema (canonical — entity-generic)
// ---------------------------------------------------------------------------

export const SmartRoutingV2Schema = z.object({
  version: z.literal(2),
  status: SmartRoutingStatusEnum.nullable(),
  /** Entity types allowed for this Smart Routing session */
  entities: z.array(SmartRoutingEntityType).min(1),
  startingLocation: StartingLocationSchema.nullable(),
  /** Entity IDs selected for route waypoints */
  selectedEntities: z.array(LocationEntitySchema).nullable(),
  routingResult: z
    .object({
      legs: z.array(EntityRoutingLegSchema),
      provider: z.string(),
    })
    .nullable(),

  /** The pre-seeded subject entity for auto-populating start location */
  subjectEntityId: z.string().nullable().optional(),
  subjectEntityType: SmartRoutingEntityType.nullable().optional(),
  hideLocationForm: z.boolean().optional(),
});
export type SmartRoutingV2 = z.infer<typeof SmartRoutingV2Schema>;

// ---------------------------------------------------------------------------
// Unified schema (discriminated by version presence)
// ---------------------------------------------------------------------------

/**
 * Accepts both v1 (legacy, version omitted or 1) and v2 (version: 2) payloads.
 * Backend normalizes v1 → v2 before processing; frontend consumes canonical v2.
 */
export const SmartRoutingSchema = z.union([SmartRoutingV2Schema, SmartRoutingV1Schema]);
export type SmartRoutingState = z.infer<typeof SmartRoutingSchema>;

/**
 * A single brand-configured "map a source field to a display column" row,
 * scoped to which entity type(s) it applies to. Mirrors the shape of the
 * `EntityColumnMapping` primitive in `@trinity/features-registry` — this
 * package intentionally doesn't depend on that package, so the shape is
 * duplicated locally, same as `SmartRoutingEntityType`'s literals.
 */
export const SmartRoutingCustomColumnSchema = z.object({
  sourceField: z.string().min(1),
  displayName: z.string().min(1),
  validEntities: z.array(SmartRoutingEntityType).min(1),
});
export type SmartRoutingCustomColumn = z.infer<typeof SmartRoutingCustomColumnSchema>;

/** Values for brand-configured custom columns, keyed by `sourceField`. Not populated for the legacy HCP index fallback. */
const CustomFieldsSchema = z
  .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
  .optional();

export const SmartRoutingAccountSchema = z.object({
  entityId: z.union([z.string(), z.number()]),
  entityType: z.literal('account'),
  name: z.string(),
  category: z.string().nullish(),
  location: LocationInfoSchema,
  locationCoords: ICoordsSchema,
  distance: z.number().nullable().optional(),
  customFields: CustomFieldsSchema,
});
export type SmartRoutingAccount = z.infer<typeof SmartRoutingAccountSchema>;

export const SmartRoutingHcpSchema = z.object({
  entityId: z.union([z.string(), z.number()]),
  entityType: z.literal('hcp'),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  location: LocationInfoSchema,
  locationCoords: ICoordsSchema,
  distance: z.number().nullable().optional(),
  specialty: z.array(z.string()).optional(),
  primaryAffiliationName: z.string().nullable().optional(),
  isKol: z.boolean().optional(),
  customFields: CustomFieldsSchema,
});
export type SmartRoutingHcp = z.infer<typeof SmartRoutingHcpSchema>;

export const SmartRoutingSearchEntitySchema = z.union([SmartRoutingAccountSchema, SmartRoutingHcpSchema]);
export type SmartRoutingSearchEntity = z.infer<typeof SmartRoutingSearchEntitySchema>;

export const SmartRoutingNearbyEntitiesFiltersSchema = z.object({
  validEntities: z.array(SmartRoutingEntityType).min(1),
  distanceRange: z
    .object({
      minMiles: z.number().min(0),
      maxMiles: z.number().min(0),
    })
    .refine((value) => value.minMiles <= value.maxMiles, {
      message: 'distanceRange.minMiles must be less than or equal to distanceRange.maxMiles',
    }),
  territoryId: z.string().optional(),
  /** Free-text filter matched across the entity name, email, and location fields. */
  searchText: z.string().trim().max(200).optional(),
});
export type SmartRoutingNearbyEntitiesFilters = z.infer<typeof SmartRoutingNearbyEntitiesFiltersSchema>;

export const SmartRoutingNearbyEntitiesInputSchema = z.object({
  brandIdentifier: z.string(),
  locationQuery: ICoordsSchema,
  filters: SmartRoutingNearbyEntitiesFiltersSchema,
});
export type SmartRoutingNearbyEntitiesInput = z.infer<typeof SmartRoutingNearbyEntitiesInputSchema>;
